import React from 'react';
import { useAppSelector } from '../../../redux/store';
import { caseSelectByQuery } from '../../../redux/thunks/DBServiceThunks';
import { createSelector } from '@reduxjs/toolkit';
import CaseItem from './CaseItem';
import { useVirtualList } from '../../../hooks/useVirtualList';
import { createFilteredListSelector, createTextSearchFilter } from '../../../redux/selectors/listSelectors';
import { Case } from '../../../models/case';
import { ScrollArea } from '../../ui/scroll-area';

interface CasesListProps {
  searchTerm: string;
}

// Memoized selector for cases
// Memoized selector for cases - Returns ALL cases available in store (Backend handles permissions)
const selectUserCases = createSelector(
  [(state) => state.DBCaseReducer],
  (caseReducer) => caseSelectByQuery(caseReducer, {})
);

// Filtered cases selector using reusable factory
const selectFilteredCases = createFilteredListSelector(
  selectUserCases,
  createTextSearchFilter((caseItem: Case) => caseItem.title)
);

/**
 * Component for displaying the list of cases with virtual scrolling
 */
const CasesList: React.FC<CasesListProps> = ({ searchTerm }) => {
  const filteredCases = useAppSelector(state => selectFilteredCases(state, searchTerm));

  const ITEM_HEIGHT = 85;
  const CONTAINER_HEIGHT = 400;
  const {
    scrollElementRef,
    visibleItems: visibleCases,
    totalHeight,
    offsetY,
    handleScroll
  } = useVirtualList(filteredCases, ITEM_HEIGHT, CONTAINER_HEIGHT);

  if (filteredCases.length === 0) return null;

  return (
    <div
      ref={scrollElementRef}
      onScroll={handleScroll}
      className="overflow-auto min-h-0 flex-1 h-full relative"
    >
      <div style={{ height: totalHeight }} className="relative">
        <div
          style={{ top: offsetY }}
          className="absolute left-0 right-0 flex flex-col gap-3 pb-2"
        >
          {visibleCases.map((caseItem) => (
            <div key={caseItem._id} style={{ minHeight: ITEM_HEIGHT }}>
              <CaseItem caseId={caseItem._id} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CasesList; 
